import { useCallback, useEffect, useState } from "react";

import type { CallScript } from "@frontierx/shared/schemas/CallScript";
import type { ScriptDetectionLogPayload } from "@frontierx/shared/schemas/clientMessageTypes";
import type { Call, LogMessage } from "@frontierx/shared/types";
import { useAuth } from "../components/Auth/UnifiedAuthProvider";

import { useAnalytics } from "../components/Analytics/AnalyticsProvider";
import { apiClient } from "../utils/apiClient";
import { useAppPartySocket } from "./useAppPartySocket";

export function useCallManager() {
  const { trackEvent } = useAnalytics();
  const [calls, setCalls] = useState<Call[]>([]);
  const [selectedCall, setSelectedCall] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<LogMessage[]>([]);
  const [scriptDetectionLogs, setScriptDetectionLogs] = useState<
    ScriptDetectionLogPayload[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const { organization, user } = useAuth();

  // Socket for real-time updates
  const mainSocket = useAppPartySocket({
    room: organization?.id ?? "waiting-room",
    onMessage(event) {
      const data = JSON.parse(event.data);
      console.log("Received message:", data);
      switch (data.type) {
        case "ACTIVE_CALLS":
          // With the API approach, we don't rely on this event anymore
          // but we keep it for backward compatibility
          console.log(
            "Received ACTIVE_CALLS event - refreshing calls from API",
          );
          refreshCalls();
          break;
        case "CALL_CREATED":
          // We now create calls via the API, but still respond to websocket notifications
          // for real-time updates from other clients
          console.log(
            "Received CALL_CREATED event - refreshing calls from API",
          );
          refreshCalls();
          // Only set selectedCall if we're not currently in a call
          if (!selectedCall) {
            setSelectedCall(data.payload.id);
          }
          break;
        case "CALL_DELETED":
          // We now delete calls via the API, but still respond to websocket notifications
          // for real-time updates from other clients
          console.log(
            "Received CALL_DELETED event - refreshing calls from API",
          );
          refreshCalls();
          if (selectedCall === data.payload.roomId) {
            setSelectedCall(null);
          }
          break;
        case "CALL_CREATION_ERROR":
          console.error("Call creation error:", data.payload.error);
          setError(data.payload.error);
          trackEvent("call_creation_error", {
            error: data.payload.error,
          });
          break;
        case "CALL_UPDATED":
          // Update a single call in the list or refresh all calls
          console.log(
            "Received CALL_UPDATED event - refreshing calls from API",
          );
          refreshCalls();
          break;
        case "LOG":
          setLogs((prevLogs) => [...prevLogs, data.payload]);
          console.log("Server log:", data.payload.message);
          break;
        case "SCRIPT_DETECTION_LOG":
          setScriptDetectionLogs((prevLogs) => [...prevLogs, data.payload]);
          console.log("Script detection log:", data.payload);
          break;
      }
    },
  });

  // Load calls from the API when the component mounts or organization changes
  const refreshCalls = useCallback(async () => {
    // Only fetch calls if we have an organization
    if (!organization?.id) return;

    setIsLoading(true);
    try {
      // Fetch calls from the API
      const response = await apiClient.getCalls({
        orgId: organization.id,
        // Only fetch calls in the last 30 days
        from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      });
      setCalls(response.calls);
    } catch (err) {
      console.error("Error fetching calls:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch calls");
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id]);

  // Load calls when the component mounts or organization changes
  useEffect(() => {
    refreshCalls();
  }, [refreshCalls]);

  // Create a new call
  const createCall = useCallback(
    async (url: string, callScript: CallScript) => {
      setError(null);

      try {
        if (!user?.id || !organization?.id) {
          throw new Error("User or organization not available");
        }

        // Calculate start/end times (default to 1 hour)
        const startTime = new Date();
        const endTime = new Date(startTime.getTime() + 60 * 60 * 1000);

        const createdCall = await apiClient.createCall({
          userId: user.id,
          orgId: organization.id,
          meetingUrl: url,
          summary: "Ad-hoc call",
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          callScriptId: callScript.id,
        });

        // Track the call creation event
        trackEvent("call_created", {
          roomId: createdCall.id,
          url: url,
          scriptId: callScript.id,
        });

        // Refresh calls after creation
        refreshCalls();

        // Set the selected call to the newly created one
        setSelectedCall(createdCall.id);
      } catch (err) {
        console.error("Error creating call via API:", err);
        setError(err instanceof Error ? err.message : "Failed to create call");
      }
    },
    [organization?.id, user?.id, refreshCalls, trackEvent],
  );

  // Delete a call
  const deleteCall = useCallback(
    async (roomId: string) => {
      try {
        await apiClient.deleteCall(roomId);

        // Update UI state after deletion
        refreshCalls();
        if (selectedCall === roomId) {
          setSelectedCall(null);
        }

        // Track deletion event
        trackEvent("call_deleted", {
          roomId: roomId,
        });
      } catch (err) {
        console.error("Error deleting call via API:", err);
        setError(err instanceof Error ? err.message : "Failed to delete call");
      }
    },
    [refreshCalls, selectedCall, trackEvent],
  );

  const selectCall = useCallback(
    (roomId: string) => {
      setSelectedCall(roomId);
      trackEvent("user_selected_call", { roomId });
    },
    [trackEvent],
  );

  const leaveCall = useCallback(() => {
    setSelectedCall(null);
    trackEvent("user_returned_to_all_calls");
  }, [trackEvent]);

  const swapCallScript = useCallback(
    async (callId: string, callScript: CallScript) => {
      try {
        // Update the call with the new script ID
        await apiClient.updateCall(callId, {
          callScriptId: callScript.id,
        });

        // Refresh calls to update the UI
        refreshCalls();

        // Track script swap event
        trackEvent("call_script_swapped", {
          callId: callId,
          scriptId: callScript.id,
        });
      } catch (err) {
        console.error("Error swapping call script via API:", err);
        setError(
          err instanceof Error ? err.message : "Failed to swap call script",
        );
      }
    },
    [refreshCalls, trackEvent],
  );

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  const clearScriptDetectionLogs = useCallback(() => {
    setScriptDetectionLogs([]);
  }, []);

  return {
    calls,
    selectedCall,
    error,
    logs,
    scriptDetectionLogs,
    isLoading,
    createCall,
    deleteCall,
    selectCall,
    leaveCall,
    clearLogs,
    clearScriptDetectionLogs,
    swapCallScript,
    refreshCalls,
    mainSocket,
  };
}
